import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Paper, Typography, Chip, Card, CardHeader, Avatar, CardMedia, Container, Slider, Box, Button } from '@mui/material';
import { stringAvatar } from '../lib/utils';
import ReusableDialog from './ReusableDialog';

const ROUNDING_UNIT_MS = 5 * 60000;
const MIN_SLIDER_UNITS = 1;
const MAX_SLIDER_UNITS = 48; // 4 hours, in 5-min units
const QUICK_DURATIONS = [15, 30, 45, 60];

// Rounds a target end time up to the next clean 5-minute mark, but never back to the start time itself.
function roundUpToNearestUnit(date, startTime) {
  const rounded = new Date(Math.ceil(date.getTime() / ROUNDING_UNIT_MS) * ROUNDING_UNIT_MS);
  return rounded > startTime ? rounded : new Date(rounded.getTime() + ROUNDING_UNIT_MS);
}

function endTimeForDuration(startTime, minutes) {
  return roundUpToNearestUnit(new Date(startTime.getTime() + minutes * 60000), startTime);
}

function endTimeForSliderUnits(startTime, units) {
  return roundUpToNearestUnit(new Date(startTime.getTime() + units * ROUNDING_UNIT_MS), startTime);
}

function formatDuration(minutes) {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins} mins`;
  if (mins === 0) return `${hours} hr${hours > 1 ? 's' : ''}`;
  return `${hours} hr${hours > 1 ? 's' : ''} ${mins} mins`;
}

function formatTime(date) {
  return date.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
}

// The earliest start of any meeting that begins at or after `from` - a booking may not run past it.
function nextMeetingStartAfter(meetings, from) {
  return (meetings ?? [])
    .map((meeting) => new Date(`${meeting.start.dateTime}Z`))
    .filter((start) => start > from)
    .sort((a, b) => a - b)[0] ?? null;
}

function RoomStatusDisplay({ roomId, roomName, roomEmail, status, currentEvent, meetings, onchange }) {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [pickerStartTime, setPickerStartTime] = useState(null);
  const [pickerEndTime, setPickerEndTime] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [bookingError, setBookingError] = useState(null);

  function dateToMelbourneTime(dateTime) {
    const date = new Date(`${dateTime}Z`);
    return date.toLocaleTimeString('en-AU', { timeZone: 'Australia/Melbourne' });
  }

  const handleClick = () => {
    if (status === 'vacant') {
      const now = new Date();
      now.setSeconds(0, 0);
      setPickerStartTime(now);
      setPickerEndTime(endTimeForDuration(now, MIN_SLIDER_UNITS * 5));
      setBookingError(null);
      setBookingOpen(true);
    } else if (status === 'occupied') {
      setReleaseOpen(true);
    }
  };

  const startTime = pickerStartTime;
  const untilTime = pickerEndTime;
  const durationMinutes = startTime && untilTime ? Math.round((untilTime.getTime() - startTime.getTime()) / 60000) : 0;
  const sliderUnits = startTime && untilTime ? Math.round((untilTime.getTime() - startTime.getTime()) / ROUNDING_UNIT_MS) : MIN_SLIDER_UNITS;
  // A kiosk booking is one the room organized for itself. Match on mailbox address, not on
  // name: the organizer name is the bare mailbox name ("Starship") while roomName is the
  // displayName ("Starship (Front Corner)"), so the old name comparison was false for every
  // room whose two forms differ. Falls back to the name only when no address is available.
  const organizerAddress = currentEvent?.organizer?.emailAddress?.address?.toLowerCase();
  const isKioskBooking = organizerAddress && roomEmail
    ? organizerAddress === roomEmail.toLowerCase()
    : currentEvent?.organizer?.emailAddress?.name === roomName;

  // A booking may run right up to the next meeting's start, but no further.
  const nextMeetingStart = startTime ? nextMeetingStartAfter(meetings, startTime) : null;
  const maxSliderUnits = nextMeetingStart
    ? Math.max(MIN_SLIDER_UNITS, Math.min(MAX_SLIDER_UNITS, Math.floor((nextMeetingStart.getTime() - startTime.getTime()) / ROUNDING_UNIT_MS)))
    : MAX_SLIDER_UNITS;
  const latestEndTime = nextMeetingStart ? endTimeForSliderUnits(startTime, maxSliderUnits) : null;
  // True when the next meeting starts so soon that even the shortest booking would overlap it.
  const noRoomToBook = Boolean(nextMeetingStart && latestEndTime && latestEndTime > nextMeetingStart);

  // Keeps a proposed end time from running over the next meeting.
  const clampEndTime = (endTime) => {
    if (!latestEndTime) return endTime;
    return endTime > latestEndTime ? latestEndTime : endTime;
  };

  const handleQuickDuration = (minutes) => {
    if (!startTime) return;
    setPickerEndTime(clampEndTime(endTimeForDuration(startTime, minutes)));
  };

  const handleSliderChange = (units) => {
    if (!startTime) return;
    setPickerEndTime(clampEndTime(endTimeForSliderUnits(startTime, units)));
  };

  // The agenda refreshes on a timer, so a booking that was valid when the dialog opened can
  // become overlapping if a new meeting appears. Pull the end time back when that happens.
  useEffect(() => {
    if (!bookingOpen || !latestEndTime || !pickerEndTime) return;
    if (pickerEndTime > latestEndTime) {
      setPickerEndTime(latestEndTime);
    }
  }, [bookingOpen, latestEndTime?.getTime(), pickerEndTime?.getTime()]);

  const handleDoneBooking = async () => {
    setIsSaving(true);
    setBookingError(null);
    const response = await fetch('/api/meetings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ roomId, durationMinutes }),
    });
    setIsSaving(false);
    if (response.ok) {
      setBookingOpen(false);
      setPickerStartTime(null);
      setPickerEndTime(null);
      onchange();
    } else if (response.status === 409) {
      // Someone booked the room between the dialog opening and Done being pressed.
      const body = await response.json().catch(() => null);
      const conflictSubject = body?.conflict?.subject;
      setBookingError(conflictSubject
        ? `That time overlaps "${conflictSubject}". Pick a shorter booking.`
        : 'That time overlaps an existing meeting. Pick a shorter booking.');
      onchange();
    } else {
      console.error('Failed to create meeting');
      setBookingError('Could not book the room. Please try again.');
    }
  };

  const handleCancelBooking = () => {
    setBookingOpen(false);
    setPickerStartTime(null);
    setPickerEndTime(null);
    setBookingError(null);
  };

  const handleReleaseMeeting = async () => {
    setReleaseOpen(false);
    const response = await fetch(`/api/meetings/${currentEvent.id}?roomId=${roomId}`, {
      method: 'DELETE',
    });
    if (response.ok) {
      onchange();
    } else {
      console.error('Failed to release meeting');
    }
  };

  return (
    <Paper sx={{minHeight: {md: '90vh', sm:'auto'} , p:2, backgroundColor: 'rgba(246, 247, 248, 0.5)'}} square={false}>
        <ReusableDialog
          open={bookingOpen} handleClose={handleCancelBooking} title="Book Room"
          actions={[
            { handler: handleCancelBooking, color: 'secondary', label: 'Cancel', disabled: isSaving },
            { handler: handleDoneBooking, color: 'primary', label: 'Done', disabled: isSaving || noRoomToBook },
          ]}
        >
          <Container sx={{ width: {md: '600px', sm:'auto'}, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '1.15rem', mb: 0.5 }}>
              Book now until
            </Typography>
            <Typography sx={{ fontSize: '2.25rem', fontWeight: 'bold', mb: 2 }}>
              {untilTime ? formatTime(untilTime) : '–'}
              <Typography component="span" sx={{ fontSize: '1.15rem', ml: 1.5, color: 'text.secondary' }}>
                ({formatDuration(durationMinutes)})
              </Typography>
            </Typography>
            {nextMeetingStart && (
              <Typography sx={{ fontSize: '1rem', mb: 1.5, color: noRoomToBook ? 'error.main' : 'text.secondary' }}>
                {noRoomToBook
                  ? `Next meeting starts at ${formatTime(nextMeetingStart)} - not enough time to book.`
                  : `Next meeting starts at ${formatTime(nextMeetingStart)}`}
              </Typography>
            )}
            {bookingError && (
              <Typography sx={{ fontSize: '1rem', mb: 1.5, color: 'error.main' }}>
                {bookingError}
              </Typography>
            )}
            <Container sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
              {QUICK_DURATIONS.map((minutes) => {
                const targetEndTime = startTime ? endTimeForDuration(startTime, minutes) : null;
                const exceedsNextMeeting = Boolean(targetEndTime && latestEndTime && targetEndTime > latestEndTime);
                const actualMinutes = startTime && targetEndTime
                  ? Math.round((targetEndTime.getTime() - startTime.getTime()) / 60000)
                  : minutes;
                const isSelected = !exceedsNextMeeting && targetEndTime && untilTime
                  && targetEndTime.getTime() === untilTime.getTime();
                return (
                  <Button
                    key={minutes}
                    variant="outlined"
                    disabled={isSaving || exceedsNextMeeting || noRoomToBook}
                    onClick={() => handleQuickDuration(minutes)}
                    sx={{
                      fontSize: '1.35rem',
                      fontWeight: 'bold',
                      py: 1.5,
                      lineHeight: 1.3,
                      border: '3px solid',
                      borderColor: isSelected ? 'primary.dark' : 'primary.main',
                      backgroundColor: isSelected ? 'primary.main' : 'transparent',
                      color: isSelected ? 'primary.contrastText' : 'primary.main',
                    }}
                  >
                    <Box>
                      {formatDuration(actualMinutes)}
                      <Typography component="div" sx={{ fontSize: '0.85rem', fontWeight: 'normal', opacity: 0.8 }}>
                        {targetEndTime ? formatTime(targetEndTime) : '–'}
                      </Typography>
                    </Box>
                  </Button>
                );
              })}
            </Container>
            <Box sx={{ px: 2 }}>
              <Slider
                value={Math.min(sliderUnits, maxSliderUnits)}
                min={MIN_SLIDER_UNITS}
                max={maxSliderUnits}
                step={1}
                onChange={(_e, value) => handleSliderChange(value)}
                valueLabelDisplay="on"
                valueLabelFormat={(units) => startTime ? formatTime(endTimeForSliderUnits(startTime, units)) : ''}
                disabled={isSaving || noRoomToBook}
                sx={{
                  height: 14,
                  '& .MuiSlider-thumb': { width: 40, height: 40 },
                  '& .MuiSlider-track': { height: 14 },
                  '& .MuiSlider-rail': { height: 14 },
                  '& .MuiSlider-valueLabel': { fontSize: '1.2rem', fontWeight: 'bold' },
                }}
              />
            </Box>
          </Container>
        </ReusableDialog>
        <ReusableDialog
          open={releaseOpen} handleClose={() => setReleaseOpen(false)} title="Release Room?"
          actions={[
            { handler: () => setReleaseOpen(false), color: 'secondary', label: 'Cancel' },
            { handler: handleReleaseMeeting, color: 'error', label: 'Release Room' },
          ]}
        >
          <Container sx={{width:{md: '400px', sm:'auto'}}}>
            <Typography>
              {isKioskBooking
                ? 'This room was booked from this display.'
                : `Booked by: ${currentEvent?.organizer?.emailAddress?.name ?? 'Unknown'}`}
            </Typography>
            {currentEvent && (
              <Typography variant="body2" color="text.secondary">
                {dateToMelbourneTime(currentEvent.start.dateTime)} - {dateToMelbourneTime(currentEvent.end.dateTime)}
              </Typography>
            )}
            {!isKioskBooking && (
              <Typography variant="body2" sx={{ mt: 2 }}>
                The organizer will be notified that the room was released.
              </Typography>
            )}
          </Container>
        </ReusableDialog>
      <Typography variant="h4">{roomName}</Typography>
      <Chip label={status === 'occupied' ? 'Click to release room' : 'Click to book room'}
            color={status === 'occupied' ? 'error' : (status === 'vacant' ? 'success' : 'primary')}
        sx={{p:5, mt:2, width: '100%', fontSize: '1.5rem', fontWeight: 'bold', '& .MuiChip-label': { fontSize: '1.5rem' }}} onClick={handleClick} />

      <Card sx={{ p:2,mt:2 }}>
        <CardHeader
        avatar={
          <Avatar aria-label="initials"  {...stringAvatar(currentEvent ? currentEvent.subject : 'A V' )} />
        }
        title={currentEvent ? (status === 'occupied' ? 'Current Event: ' + currentEvent.subject : ' '):'Available to use'}
        subheader={currentEvent ? `${dateToMelbourneTime(currentEvent.start.dateTime)} - ${dateToMelbourneTime(currentEvent.end.dateTime)}` : ''}
      />
      <CardMedia
        component="img"
        height="200"
        image={status === 'occupied' ? "/inuse_meeting_room_with_oval_desk.png" : "/empty_meeting_room_with_oval_desk.png"}
        alt="Meeting room"
      />
      </Card>

    </Paper>
  );
}

RoomStatusDisplay.propTypes = {
  roomId: PropTypes.string,
  roomName: PropTypes.string,
  roomEmail: PropTypes.string,
  status: PropTypes.string,
  currentEvent: PropTypes.object,
  meetings: PropTypes.arrayOf(PropTypes.shape({
    subject: PropTypes.string,
    start: PropTypes.shape({ dateTime: PropTypes.string }),
    end: PropTypes.shape({ dateTime: PropTypes.string }),
  })),
  onchange: PropTypes.func,
};

export default RoomStatusDisplay;
